// A tab that stays open across a deploy asks for chunks the deploy removed. One reload gets the new build.
// The entry URL changes with each build. A chunk that still fails after a reload into the same build lets
// the error through to the error boundary, so a missing chunk cannot cause a reload loop.
const RELOADED_BUILD_KEY = 'chunk-error-reloaded-build';
// A `beforeunload` prompt can cancel the reload. If the page is still open after this time, the imports that
// wait for the reload fail with the chunk error, so the error boundaries can show a retry.
export const RELOAD_TIMEOUT_MS = 10_000;
const build = import.meta.url;

let pendingReload: Promise<void> | undefined;

const reloadedThisBuild = () => {
  try {
    return window.sessionStorage.getItem(RELOADED_BUILD_KEY) === build;
  } catch {
    return true;
  }
};

const markReload = () => {
  try {
    window.sessionStorage.setItem(RELOADED_BUILD_KEY, build);
    return true;
  } catch {
    return false;
  }
};

const startReload = () => {
  if (pendingReload || reloadedThisBuild() || !markReload()) {
    return pendingReload;
  }
  pendingReload = new Promise(resolve => {
    setTimeout(() => {
      pendingReload = undefined;
      resolve();
    }, RELOAD_TIMEOUT_MS);
  });
  window.location.reload();
  return pendingReload;
};

// Only an import that the user starts (a route, a dialog) reloads the page. Other imports keep their own
// error handling, so a background import cannot reload the page while the user works.
export const holdWhileReloading =
  <T>(importer: () => Promise<T>) =>
  (): Promise<T> =>
    importer().catch((error: unknown) => {
      const reload = isChunkLoadError(error) ? startReload() : undefined;
      if (!reload) {
        throw error;
      }
      return reload.then(() => {
        throw error;
      });
    });

// TanStack's `lazyRouteComponent` does its own reload for errors with the browser's chunk error messages.
// This error has a different message, so the reload guard in this file is the only one.
export class ChunkLoadError extends Error {
  constructor(cause: unknown) {
    super('A code chunk did not load', { cause });
    this.name = 'ChunkLoadError';
  }
}

// The browser messages for a failed `import()`, and Vite's message when the CSS of a chunk does not load.
const CHUNK_ERROR_MESSAGE =
  /^(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for)/;

export const isChunkLoadError = (error: unknown) =>
  error instanceof ChunkLoadError || (error instanceof Error && CHUNK_ERROR_MESSAGE.test(error.message));
